/* eslint-disable @next/next/no-img-element */
'use client'
import { Icon, Image } from '@/components'
import { IProductGalleryImage } from '@/features/products/types'
import { useWindowSize } from '@/hooks/use-window-size'
import { Fancybox } from '@fancyapps/ui'
import '@fancyapps/ui/dist/fancybox/fancybox.css'
import { alpha, Box, IconButton, styled, SxProps } from '@mui/material'
import { AnimatePresence } from 'framer-motion'
import maxBy from 'lodash/maxBy'
import { useQueryState } from 'nuqs'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { Autoplay, FreeMode, Navigation, Thumbs } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { RenderImage } from './image-item'
import { MobileGalleryModal } from './modal-gallery-mobile'
import { SwiperImagesFeedback } from './swiper-images-feedback'
import { IMG_PLACEHOLDER, IMG_PLACEHOLDER_SQUARE } from '@/constants/ui'

type LeftSideDetailProductsProps = {
  sx?: SxProps
  galleryImages?: IProductGalleryImage
  data?: Product
}
export type CommonTypeImage = { src: string; zoomAble: boolean; zoomBasic?: boolean; preload?: boolean }

const StyledSwiperThumb = styled(Swiper)(({ theme }) => ({
  // padding: theme.spacing(1, 0),
  width: '100%',
  height: '100%',

  //hidden swiper-button-prev và swiper-button-next, khi hover thì hiện
  '& .swiper-button-prev, & .swiper-button-next': {
    opacity: 0,
    width: 28,
    height: 28,
    top: '50%',
    transition: 'opacity 0.3s ease',
  },
  '&:hover .swiper-button-prev, &:hover .swiper-button-next': {
    opacity: 'inherit',
    '&.disabled-nav-button': {
      opacity: 0.3,
      cursor: 'default',
      pointerEvents: 'none',
    },
  },
}))

const StyledSwiperSlide = styled(SwiperSlide)(({ theme }) => ({
  aspectRatio: '1/1',
  height: 'calc(100% - 2px)',
  cursor: 'pointer',
  overflow: 'hidden',
  borderRadius: '6px',
  border: '1px solid',
  borderColor: alpha(theme.palette.primary.main, 0.3),
  '&.swiper-slide-thumb-active': {
    borderColor: `${theme.palette.five.main} !important`,
  },
  width: '80px',
  [theme.breakpoints.down('md')]: {
    width: '60px',
  },
}))
export const LeftSideDetailProducts = ({ data }: LeftSideDetailProductsProps) => {
  const { isMobile } = useWindowSize()
  const [showPopupFeedback, setShowPopupFeedback] = useState(false)
  const [color, setColor] = useQueryState('color')
  useEffect(() => {
    let currentTimeout = setTimeout(() => {
      setShowPopupFeedback(true)
    }, 1000)

    return () => {
      clearTimeout(currentTimeout)
    }
  }, [])

  const allImages = useMemo(() => {
    const images: CommonTypeImage[] = []
    const rawImages: string[] = []
    let variantWithMostImages: IVariantsProduct | undefined
    if (!color) {
      // Nếu không có color trong query, lấy biến thể đầu tiên
      variantWithMostImages = maxBy(data?.variants, (v) => v?.product?.media_gallery?.length ?? 0)
    } else {
      // tìm danh sách sản phẩm cùng màu
      const relatedProducts = data?.variants?.filter((variant) => {
        const colorAttr = variant?.attributes?.find((attr) => attr.code === 'color')
        return colorAttr?.uid === color
      })
      // tìm biến thể có danh sách ảnh media_gallery dài nhất
      variantWithMostImages = maxBy(relatedProducts, (v) => v?.product?.media_gallery?.length ?? 0)
    }

    // sort list media_gallery by position field then push to images array
    const tempData = variantWithMostImages?.product?.media_gallery?.sort((a, b) => a?.position - b?.position)
    tempData?.forEach((item, index) => {
      images.push({
        src: item?.url,
        zoomAble: true,
        preload: index === 0,
      })
      rawImages.push(item?.url)
    })

    return {
      withZoomable: images,
      raw: rawImages,
    }
  }, [color, data?.variants])

  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)
  const [mainSwiper, setMainSwiper] = useState<SwiperType | null>(null)

  const handleStopAutoplay = () => {
    if (mainSwiper?.autoplay) {
      mainSwiper.autoplay.stop()
    }
  }

  const handleStartAutoplay = () => {
    if (mainSwiper?.autoplay) {
      mainSwiper.autoplay.start()
    }
  }

  return (
    <>
      {data?.id && !isMobile && showPopupFeedback && <SwiperImagesFeedback data={data} />}
      <Box display={'flex'} flexDirection={'column-reverse'} gap={1} className="swiper-detail">
        <Box
          sx={{
            width: {
              xs: '100%',
            },
            height: {
              xs: '60px',
              md: '80px',
            },
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
            }}
          >
            <StyledSwiperThumb
              onSwiper={setThumbsSwiper}
              slidesPerView={'auto'}
              freeMode={true}
              watchSlidesProgress={true}
              spaceBetween={8}
              direction="horizontal"
              navigation={{
                enabled: true,
                disabledClass: 'disabled-nav-button',
              }}
              modules={[FreeMode, Navigation, Thumbs]}
              sx={{
                '& .swiper-button-prev, & .swiper-button-next': {
                  top: '60%',
                },
              }}
            >
              {allImages?.raw?.length > 0 ? (
                allImages?.raw?.map((item, index) => (
                  <StyledSwiperSlide key={index} virtualIndex={index}>
                    <img
                      src={item || ''}
                      alt="Thumbnail image"
                      width={'100%'}
                      height={'100% '}
                      style={{ aspectRatio: '1/1', objectFit: 'contain' }}
                    />
                  </StyledSwiperSlide>
                ))
              ) : (
                <StyledSwiperSlide virtualIndex={0}>
                  <img
                    src={IMG_PLACEHOLDER_SQUARE}
                    alt="Thumbnail image"
                    width={'100%'}
                    height={'100%'}
                    style={{ aspectRatio: '1/1', objectFit: 'contain' }}
                  />
                </StyledSwiperSlide>
              )}
              {/* {allImages?.raw?.map((item, index) => (
                <StyledSwiperSlide key={index} virtualIndex={index}>
                  <img src={item || ''} alt="Thumbnail image" />
                </StyledSwiperSlide>
              ))} */}
            </StyledSwiperThumb>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <Box
            sx={{
              aspectRatio: '1/1',
              borderRadius: '6px',
              overflow: 'hidden',
            }}
          >
            <StyledSwiperThumb
              style={{ width: '100%', height: '100%' }}
              slidesPerView={'auto'}
              navigation={{
                enabled: true,
              }}
              thumbs={{ swiper: thumbsSwiper }}
              autoplay={{
                delay: 8000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              onSwiper={setMainSwiper}
              modules={[FreeMode, Navigation, Thumbs, Autoplay]}
            >
              {allImages?.withZoomable?.length > 0 ? (
                allImages?.withZoomable?.map((item, index) => (
                  <SwiperSlide key={index}>
                    <RenderImage
                      item={item}
                      index={index}
                      allImages={allImages}
                      handleStopAutoplay={handleStopAutoplay}
                      handleStartAutoplay={handleStartAutoplay}
                    />
                  </SwiperSlide>
                ))
              ) : (
                <SwiperSlide>
                  <img
                    src={IMG_PLACEHOLDER_SQUARE}
                    alt="Thumbnail image"
                    width={'100%'}
                    height={'100%'}
                    style={{ objectFit: 'contain' }}
                  />
                </SwiperSlide>
              )}
            </StyledSwiperThumb>
          </Box>
        </Box>
      </Box>
    </>
  )
}

//#region Zoomable Image
// Component xử lý zoom ảnh - COMPLETELY FIXED VERSION
export const ZoomableImage = ({
  src,
  alt,
  isShowZoomBasic = false,
  allImages,
  currentIndex,
}: {
  src: string
  alt: string
  isShowZoomBasic: boolean
  allImages: string[]
  currentIndex: number
}) => {
  const { isMobile } = useWindowSize()
  const [isHovering, setIsHovering] = useState(false)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [showMobileGallery, setShowMobileGallery] = useState(false)
  const zoomContainerRef = useRef<HTMLDivElement>(null)
  const zoomFactor = 2.2 // Độ phóng đại của ảnh
  const transitionDuration = 0.3 // Thời gian transition (giây)

  // Xử lý sự kiện khi di chuột vào ảnh
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!zoomContainerRef.current) return

    const { left, top, width, height } = zoomContainerRef.current.getBoundingClientRect()

    // Tính toán vị trí chuột trong ảnh (0 đến 1)
    const x = (e.clientX - left) / width
    const y = (e.clientY - top) / height

    // Giới hạn giá trị x, y từ 0 đến 1
    const boundedX = Math.max(0, Math.min(1, x))
    const boundedY = Math.max(0, Math.min(1, y))

    setMousePosition({ x: boundedX, y: boundedY })
  }

  const handleMouseEnter = () => {
    setTimeout(() => {
      setIsHovering(true)
    }, 100)
  }

  const handleMouseLeave = () => {
    setTimeout(() => {
      setIsHovering(false)
    }, 100)
  }

  const handleShowZoomBasic = () => {
    if (!isShowZoomBasic) return

    if (isMobile) {
      setShowMobileGallery(true)
    } else {
      Fancybox.show(
        allImages.map((src) => ({ src })),
        { startIndex: currentIndex }
      )
    }
  }

  const isApplyAnimation = isHovering && !isMobile

  // Kích thước của khung preview
  const previewSize = 120

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        // aspectRatio: '1/1',
        height: '100%',
        overflow: 'hidden',
        cursor: 'zoom-in',
      }}
      ref={zoomContainerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      {/* Ảnh gốc */}
      <Image
        onClick={handleShowZoomBasic}
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transition: `transform ${transitionDuration}s ease-out`,
          transform: isApplyAnimation ? `scale(${zoomFactor})` : 'scale(1)',
          transformOrigin: `${mousePosition.x * 100}% ${mousePosition.y * 100}%`,
        }}
      />

      {/* Hiển thị ảnh gốc với vùng đang được xem ở góc dưới bên trái */}
      {!isMobile && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            width: `${previewSize}px`,
            height: `${previewSize}px`,
            overflow: 'hidden',
            zIndex: 10,
            borderRadius: '4px',
            opacity: isHovering ? 1 : 0,
            transition: 'opacity 0.4s ease',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              position: 'relative',
            }}
          >
            {/* Layer 1: Ảnh nền đầy đủ */}
            <Image
              src={src}
              alt={`${alt}-background`}
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                inset: 0,
                zIndex: 1,
              }}
            />

            {/* Layer 2: Overlay tối che toàn bộ */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 2,
              }}
            />

            {/* Layer 3: Khung clip-path hiển thị vùng sáng (phần đang zoom) */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 3,
                clipPath: `inset(
                  ${Math.max(0, mousePosition.y * 100 - 100 / zoomFactor / 2)}% 
                  ${Math.max(0, 100 - mousePosition.x * 100 - 100 / zoomFactor / 2)}% 
                  ${Math.max(0, 100 - mousePosition.y * 100 - 100 / zoomFactor / 2)}% 
                  ${Math.max(0, mousePosition.x * 100 - 100 / zoomFactor / 2)}%
                )`,
                transition: 'clip-path 0.1s ease-out',
              }}
            >
              {/* Ảnh sáng - phần không bị làm tối */}
              <Image
                src={src}
                alt={`${alt}-highlight`}
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  inset: 0,
                }}
              />

              {/* Border đỏ cho vùng đang xem */}
              {/* <Box
                sx={{
                  position: 'absolute',
                  inset: '1px',
                  border: '1.5px solid #ff0000',
                  pointerEvents: 'none',
                }}
              /> */}
            </Box>
          </Box>
        </Box>
      )}

      {/* Chức năng zoom đầy đủ khi click */}
      {isShowZoomBasic && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            left: '50%',
            zIndex: 2,
            opacity: isHovering ? 1 : 0,
            transition: 'opacity 0.4s ease',
            pointerEvents: isHovering ? 'auto' : 'none',
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: isHovering ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.7)',
              backgroundColor: 'primary.main',
              padding: '12px',
              borderRadius: '999px',
              zIndex: -1,
              transition: 'all 0.4s ease',
              color: 'primary.contrastText',
              ':hover': { backgroundColor: 'primary.dark' },
            }}
            onClick={handleShowZoomBasic}
          >
            <Icon.SearchV2 sx={{ fontSize: '1rem', aspectRatio: '1/1', color: 'inherit' }} />
          </IconButton>
        </Box>
      )}

      {isMobile && showMobileGallery && (
        <AnimatePresence mode="wait">
          <MobileGalleryModal
            images={allImages?.map((src) => ({ src, zoomAble: false })) || []}
            onClose={() => setShowMobileGallery(false)}
            isShow={showMobileGallery}
            initialIndex={currentIndex}
          />
        </AnimatePresence>
      )}
    </Box>
  )
}

//#region Zoom Basic
export const ZoomBasic = ({
  src,
  alt,
  currentIndex,
  allImages,
}: {
  src: string
  alt: string
  currentIndex: number
  allImages: string[]
}) => {
  const { isMobile } = useWindowSize()
  const [showMobileGallery, setShowMobileGallery] = useState(false)

  const handleZoomClick = () => {
    if (isMobile) {
      setShowMobileGallery(true)
    } else {
      Fancybox.show(
        allImages?.map((src) => ({ src })),
        { startIndex: currentIndex }
      )
    }
  }

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1/1',
          overflow: 'hidden',
          cursor: 'pointer',
          '& .zoom-button-wrapper': {
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.4s ease',
          },
          '&:hover .zoom-button-wrapper': {
            opacity: 1,
            pointerEvents: 'auto',
          },
          '& .zoom-icon-button': {
            transform: 'translate(-50%, -50%) scale(0.7)',
            transition: 'all 0.4s ease',
          },
          '&:hover .zoom-icon-button': {
            transform: 'translate(-50%, -50%) scale(1)',
          },
        }}
        onClick={handleZoomClick}
      >
        <Image src={src} alt={alt} sx={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover' }} />
        {/* Chức năng zoom đầy đủ khi click */}
        <Box
          className="zoom-button-wrapper"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
          }}
        >
          <IconButton
            className="zoom-icon-button"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              backgroundColor: 'primary.main',
              padding: '12px',
              borderRadius: '999px',
              zIndex: -1,
              color: 'primary.contrastText',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            <Icon.SearchV2 sx={{ fontSize: '1rem', aspectRatio: '1/1', color: 'inherit' }} />
          </IconButton>
        </Box>
      </Box>

      {isMobile && showMobileGallery && (
        <AnimatePresence mode="wait">
          <MobileGalleryModal
            images={allImages?.map((src) => ({ src, zoomAble: false })) || []}
            onClose={() => setShowMobileGallery(false)}
            initialIndex={currentIndex}
            isShow={showMobileGallery}
          />
        </AnimatePresence>
      )}
    </>
  )
}
